// src/homepage/IntroSection.tsx
import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useInView, Variants } from 'framer-motion';

// Animations
const float = keyframes`
    0%, 100% {
        transform: translateY(0px);
    }
    50% {
        transform: translateY(-10px);
    }
`;

const moveLeftToRight = keyframes`
    0% {
        left: -150px;
        transform: translateY(0);
    }
    50% {
        transform: translateY(-8px);
    }
    100% {
        left: calc(100% + 150px);
        transform: translateY(0);
    }
`;

// Styled Components
const IntroContainer = styled(motion.section)`
    background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.2)),
    url('/hero.png') center/cover no-repeat;
    padding: 5rem 2rem 6rem 2rem;
    position: relative;
    overflow: hidden;
    min-height: 90vh;
    display: flex;
    align-items: center;
    justify-content: center;
    @media (max-width: 768px) {
        padding: 4rem 1.5rem 5rem 1.5rem;
        min-height: 90vh;
    }
`;

const ContentOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
            135deg,
            rgba(0, 0, 0, 0.58) 0%,
            rgba(0, 0, 0, 0.53) 50%,
            rgba(0, 0, 0, 0.3) 100%
    );
    z-index: 1;
`;

const IntroWrapper = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6rem;
    align-items: center;
    position: relative;
    z-index: 2;
    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 4rem;
        text-align: center;
    }
    @media (max-width: 768px) {
        gap: 3rem;
    }
`;

const TextContent = styled(motion.div)`
    position: relative;
    z-index: 3;
`;

const Title = styled(motion.h1)`
    font-size: 4rem;
    font-weight: 800;
    line-height: 1.1;
    color: #FFFFFF;
    margin-bottom: 2rem;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    position: relative;
    &::after {
        content: '';
        position: absolute;
        bottom: -15px;
        left: 0;
        width: 100px;
        height: 4px;
        background: linear-gradient(90deg, #FFFFFF, #C0C0C0);
        border-radius: 2px;
        @media (max-width: 968px) {
            left: 50%;
            transform: translateX(-50%);
        }
    }
    @media (max-width: 1024px) {
        font-size: 3.5rem;
    }
    @media (max-width: 768px) {
        font-size: 3rem;
    }
    @media (max-width: 480px) {
        font-size: 2.5rem;
    }
`;

const Subtitle = styled(motion.p)`
    font-size: 1.4rem;
    line-height: 1.8;
    color: rgba(203, 203, 203, 0.95);
    margin-bottom: 3rem;
    max-width: 600px;
    font-weight: 400;
    text-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
    @media (max-width: 768px) {
        font-size: 1.2rem;
        margin-bottom: 2.5rem;
        max-width: none;
    }
    @media (max-width: 480px) {
        font-size: 1.1rem;
    }
`;

const ButtonsWrapper = styled(motion.div)`
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
    @media (max-width: 968px) {
        justify-content: center;
    }
    @media (max-width: 768px) {
        gap: 1rem;
    }
`;

const ButtonBase = styled(motion.a)`
    padding: 1.2rem 2.5rem;
    font-size: 1.1rem;
    border-radius: 50px;
    text-decoration: none;
    font-weight: 600;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    position: relative;
    overflow: hidden;
    border: none;
    cursor: pointer;
    text-align: center;
    backdrop-filter: blur(10px);
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
        transition: left 0.6s;
    }
    &:hover::before {
        left: 100%;
    }
    @media (max-width: 768px) {
        padding: 1rem 2rem;
        font-size: 1rem;
    }
`;

const LearnMoreButton = styled(ButtonBase)`
    border: 2px solid rgba(255, 255, 255, 0.8);
    color: #FFFFFF;
    background: transparent;
    &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: #FFFFFF;
        box-shadow: 0 8px 25px rgba(255, 255, 255, 0.2);
        transform: translateY(-3px);
    }
`;

const FloatingElement = styled.div<{ delay: string; top: string; left: string }>`
    position: absolute;
    width: 120px;
    height: 120px;
    top: ${props => props.top};
    left: ${props => props.left};
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    animation: ${float} 6s ease-in-out infinite;
    animation-delay: ${props => props.delay};
    z-index: 1;
    @media (max-width: 768px) {
        width: 80px;
        height: 80px;
    }
`;

const LogosWrapper = styled.div`
    position: absolute;
    bottom: 12%;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: 900px;
    overflow: hidden;
    z-index: 3;
    height: 60px;
`;

// Moving Logos - Left to Right
const MovingLogo = styled(motion.div)<{ delay: string }>`
    position: absolute;
    top: 0;
    z-index: 3;
    filter: brightness(0) invert(1);
    opacity: 0.6;
    animation: ${moveLeftToRight} 20s linear infinite;
    animation-delay: ${props => props.delay};

    &:hover {
        opacity: 1;
        filter: brightness(0) invert(1) drop-shadow(0 0 12px rgba(255, 255, 255, 0.7));
        animation-play-state: paused;
    }

    img {
        height: 50px;
        width: auto;
        object-fit: contain;

        @media (max-width: 768px) {
            height: 40px;
        }

        @media (max-width: 480px) {
            height: 30px;
        }
    }
`;

// Define proper variants with correct typing
const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.3
        }
    }
};

const itemVariants: Variants = {
    hidden: {
        opacity: 0,
        y: 30
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.7,
            ease: "easeOut"
        }
    }
};

const Hero: React.FC = () => {
    const containerRef = React.useRef(null);
    const isInView = useInView(containerRef, { once: true });

    const logos = [
        { name: 'evita', src: '/evita.png' },
        { name: 'joj', src: '/joj.png' },
        { name: 'markiza', src: '/markiza.png' },
        { name: 'rtvs', src: '/rtvs.png' },
        { name: 'ta3', src: '/ta3.png' }
    ];

    const delays = ['0s', '-4s', '-8s', '-12s', '-16s'];

    return (
        <IntroContainer
            ref={containerRef}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={containerVariants}
        >
            <ContentOverlay />
            {/* Floating Background Elements */}
            <FloatingElement delay="0s" top="20%" left="10%" />
            <FloatingElement delay="2s" top="70%" left="80%" />
            <FloatingElement delay="4s" top="40%" left="85%" />
            <FloatingElement delay="1s" top="80%" left="15%" />

            <IntroWrapper>
                <TextContent>
                    <Title variants={itemVariants}>
                        MUDr. Soňa Belanská, psychiatrička
                    </Title>
                    <Subtitle variants={itemVariants}>
                        Telesné a duševné zdravie sú dve spojené nádoby. Hoci sa o dôležitosti duševného zdravia veľa píše a hovorí, mnohí ľudia stále trpia bez pomoci, potichu, nezriedka osamote, zbierajú v sebe odhodlanie navštíviť odborníka.
                    </Subtitle>
                    <ButtonsWrapper variants={itemVariants}>
                        <LearnMoreButton
                            href="/o-mne"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Viac informácií
                        </LearnMoreButton>
                    </ButtonsWrapper>
                </TextContent>
            </IntroWrapper>

        </IntroContainer>
    );
};

export default Hero;